import { Logger } from 'aws-amplify';
import jsPDF from 'jspdf';
import { toCanvas } from 'html-to-image';
import { S3Utils } from '..';
import {
  SignatureComponentType,
  ESIGNATURE_PAGE_MARGIN,
  SIGNATURE_COMPONENT_DEFAULT_HEIGHT,
  SIGNATURE_COMPONENT_DEFAULT_WIDTH,
} from 'src/constants';
import {
  SignaturePageComponent,
  SignaturePageImageData,
} from 'src/store/signaturePage/types';
import { AlefRegularFont } from 'src/constants/fontConsts/Alef-Regular-normal';
import { FieldType } from 'src/services/api';
import { ensureUnreachable } from '../CommonUtils';

const logger = new Logger('SignatureUtils');

// check if string is right-to-left based on regex with matches
// if string starts with left characters followed by right characters
const isR2L = (s: string) => {
  const ltrChars =
    'A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02B8\u0300-\u0590\u0800-\u1FFF' +
    '\u2C00-\uFB1C\uFDFE-\uFE6F\uFEFD-\uFFFF';
  const rtlChars = '\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC';
  // eslint-disable-next-line no-misleading-character-class
  const rtlDirCheck = new RegExp(`^[^${ltrChars}]*[${rtlChars}]`);

  return rtlDirCheck.test(s);
};

export const numberWithCommas = (value: number) =>
  value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * check that every request component type has a value
 * @param components pdf components
 * @param isV2 true for e-signature v2
 */
export const isEsigCompleted = (components: SignaturePageComponent[]) =>
  components
    .filter(comp => comp.componentType.startsWith('request'))
    .every(comp => comp.isSkipped || comp.value !== ''); // if component is optional then it's ok if the value is empty

/**
 * Load a signature pdf page imageElement from the given url, this url is generated
 * based on the s3 bucket location when the fileEntity is loaded
 */
export const loadPageImage = async (
  pageImageUrl: string,
): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const pageImageElement = new Image();
    pageImageElement.crossOrigin = 'Anonymous';
    pageImageElement.onload = () => resolve(pageImageElement);
    pageImageElement.onerror = reject;
    pageImageElement.src = pageImageUrl;
  });

/**
 * Convert an image to a data string
 * @param imgElement html image with src to load
 * @returns image data
 */
export const getImageData = (imgElement: HTMLImageElement) => {
  const canvas = document.createElement('canvas');
  canvas.width = imgElement.width;
  canvas.height = imgElement.height;
  const ctx = canvas.getContext('2d');
  let imageData = '';
  if (ctx) {
    ctx.drawImage(imgElement, 0, 0);
    imageData = canvas.toDataURL('image/jpeg');
  }
  return imageData;
};

/**
 * Get the position for a component. It calculated as the overall yposition in the entire doc
 * subtracted by any pageOffset (margins) and sum of previous page heights
 * @param prevPagesHeight
 * @param pageNumber
 * @param yPosition
 * @param offset
 */
export const getYPosition = (
  prevPagesHeight: number,
  pageNumber: number,
  yPosition: number,
  offset: number,
) => {
  const pageOffset = (pageNumber - 1) * ESIGNATURE_PAGE_MARGIN; // sum of margins between previous pages

  // Here we are calculating the y position of the sign component relative to the
  // page it is placed on.
  // Here is an example to understand how it works
  // Consider we have a pdf of 3 pages with uneven heights 100,90,100
  // And a sign component is placed on 3rd page with yPosition = 260
  // Now our "prevPagesHeight" = page 1 height + page 2 height = 100 + 90 = 190
  // And our "pageOffset" i.e. sum of prev page margins = (3 - 1) * ESIGNATURE_PAGE_MARGIN = 30
  // For this example let "offset" be 0. But in order to incorporate text sign components
  // we can pass offset = 15.
  // Now, y = 260 + 0 - (190 + 30) = 40(y position relative to 3rd page)
  // refer to [diagram](./yPositionDiagram.png)
  const y = yPosition + offset - (prevPagesHeight + pageOffset);
  return y;
};

/**
 * Take a list of page images and components that are drawn on them to generate
 * a pdf doc representing the signed document. That doc is then uploaded to s3
 * in a sub-directory using the original filekey
 * @param pageImages list of page image items that are the canvas
 * @param pageComponents components drawn on the images
 * @param fileKey s3 key of orignal file
 */
export const generateSignaturePDF = async (
  pageImages: SignaturePageImageData[],
  fileKey: string,
  pageComponents?: SignaturePageComponent[],
  JsPDF?: typeof jsPDF,
) => {
  // first generate pdf doc
  if (!JsPDF || !pageComponents) {
    return;
  }

  let [summaryPageWidth, summaryPageHeight] = [0, 0];
  const pageImagesWithElement = await Promise.all(
    pageImages.map(async pageImage => {
      const pageImageElement = await loadPageImage(pageImage.url);
      const imageData = getImageData(pageImageElement);

      return { ...pageImage, imgElement: pageImageElement, imageData };
    }),
  );

  const pdfDoc = pageImagesWithElement.reduce(
    (startingDoc, pageImage, pageIndex) => {
      const { imgElement: pageImageElement, imageData } = pageImage;
      const pageSizeMultiplier: number = 680 / pageImageElement.width;
      const pageWidth = pageImageElement.width * pageSizeMultiplier;
      const pageHeight = pageImageElement.height * pageSizeMultiplier;

      // all previous pages
      const allPrevPages = pageImagesWithElement.filter(
        p => p.pageNumber < pageImage.pageNumber,
      );

      // sum of heights of all the prev pages
      const prevPagesHeight = allPrevPages.reduce((sum, page) => {
        const { height, width } = page.imgElement;
        const sizeMultiplier = 680 / width;
        return sum + height * sizeMultiplier;
      }, 0);

      logger.debug('Setting page', pageIndex, 'height (px) to', pageHeight);
      let doc = startingDoc;
      if (pageIndex === 0) {
        doc = new JsPDF({
          unit: 'pt',
          format: [Math.round(pageWidth), Math.round(pageHeight)],
          compress: true,
        });
        doc.addFileToVFS('Alef-Regular.ttf', AlefRegularFont);
        doc.addFont('Alef-Regular.ttf', 'Alef-Regular', 'normal');
        doc.setFont('Alef-Regular');
      } else {
        doc.addPage([Math.round(pageWidth), Math.round(pageHeight)]);
      }

      try {
        doc.addImage(
          imageData,
          'JPEG',
          0,
          0,
          doc.internal.pageSize.getWidth(),
          doc.internal.pageSize.getHeight(),
          undefined,
          'FAST',
        );
      } catch (e) {
        logger.error(e, 'error adding page image to pdf', pageImageElement);
      }

      const pageImageComponents = pageComponents.filter(
        pageComponent => pageComponent.page === pageImage.pageNumber,
      );
      pageImageComponents.forEach(pageComponent => {
        const docPageSize = doc.internal.pageSize.getHeight();
        switch (pageComponent.componentType) {
          case SignatureComponentType.OWNER_INITIAL:
          case SignatureComponentType.OWNER_SIGN:
          case SignatureComponentType.REQUEST_INITIAL:
          case SignatureComponentType.REQUEST_SIGN: {
            // draw initial/signature onto existing document as image
            const position = getYPosition(
              prevPagesHeight,
              pageImage.pageNumber,
              pageComponent.yPosition,
              0,
            );

            logger.debug(
              'Set y-position to',
              position,
              'based on page height and number:',
              docPageSize,
              pageImage.pageNumber,
              'for component',
              pageComponent,
            );
            const sigImg = new Image();
            sigImg.src = pageComponent.value || '';
            // We only want to set width and height for image if they are not zero.
            if (pageComponent.height > 0) sigImg.height = pageComponent.height;
            if (pageComponent.width > 0) sigImg.width = pageComponent.width;
            if (pageComponent.componentType.startsWith('request')) {
              sigImg.height =
                pageComponent.resizedHeight ||
                SIGNATURE_COMPONENT_DEFAULT_HEIGHT;
              sigImg.width =
                pageComponent.resizedWidth || SIGNATURE_COMPONENT_DEFAULT_WIDTH;
            }
            try {
              doc.addImage(
                sigImg,
                'PNG',
                pageComponent.xPosition,
                position,
                sigImg.width,
                sigImg.height,
              );
            } catch (e) {
              logger.error(e, 'error adding signature image to pdf', sigImg);
            }
            break;
          }
          case SignatureComponentType.OWNER_TEXT:
          case SignatureComponentType.REQUEST_TEXT:
          case SignatureComponentType.OWNER_DATE:
          case SignatureComponentType.REQUEST_DATE: {
            console.info('Adding text to signature page', pageComponent.value);
            if (pageComponent.value) {
              doc.setR2L(isR2L(pageComponent.value));
            }
            doc.text(
              pageComponent.value || '',
              pageComponent.xPosition,
              getYPosition(
                prevPagesHeight,
                pageImage.pageNumber,
                pageComponent.yPosition,
                15,
              ), // incorporate offset for text fields
              {
                maxWidth: pageComponent.width || 150,
              },
            );
            doc.setR2L(false); // reset r2l after text is added
            break;
          }
          default:
            break;
        }
      });

      summaryPageWidth = pageWidth;
      summaryPageHeight = pageHeight;
      return doc;
    },
    new JsPDF(),
  );

  // if this is the last page add another page to contain
  // the signature events history
  const input = document.getElementById('signaturePageSummary');
  if (input && summaryPageWidth && summaryPageHeight) {
    const canvas = await toCanvas(input);
    pdfDoc.addPage([
      Math.round(summaryPageWidth),
      Math.round(summaryPageHeight),
    ]);

    const imgHeight = (canvas.height * summaryPageWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/jpeg', 1.0);
    try {
      pdfDoc.addImage(
        imgData,
        'JPEG',
        0,
        0,
        summaryPageWidth,
        imgHeight,
        undefined,
        'FAST',
      );
    } catch (e) {
      logger.error(e, 'error adding signature history to pdf', imgData);
    }
  }
  // upload generated pdf doc to s3
  const newPath = `${fileKey}/signed.pdf`;
  await S3Utils.uploadFile(newPath, pdfDoc.output('blob') as File, {
    level: 'protected',
  });
};

/**
 * Checks if there are any components added for the client to sign.
 * @param pageComponents - The array of signature page components.
 * @returns `true` if client components exist, `false` otherwise.
 */
export const hasValidClientComponents = (
  pageComponents: SignaturePageComponent[],
) => {
  const clientPageComponents =
    pageComponents?.filter(component =>
      component.componentType.startsWith('request'),
    ) || [];

  return clientPageComponents.length > 0;
};

/**
 * Retrieves the field type of a component based on its component type.
 * @param componentType - The type of the component.
 * @returns The corresponding FieldType for the given componentType.
 */
export const getFieldTypeUsingComponentType = (
  componentType: SignatureComponentType,
): FieldType => {
  switch (componentType) {
    case SignatureComponentType.REQUEST_DATE:
    case SignatureComponentType.OWNER_DATE:
      return FieldType.Date;
    case SignatureComponentType.REQUEST_SIGN:
    case SignatureComponentType.OWNER_SIGN:
      return FieldType.Signature;
    case SignatureComponentType.REQUEST_TEXT:
    case SignatureComponentType.OWNER_TEXT:
      return FieldType.Text;
    case SignatureComponentType.REQUEST_INITIAL:
    case SignatureComponentType.OWNER_INITIAL:
      return FieldType.Initials;
    default:
      return ensureUnreachable(componentType);
  }
};

/**
 * We use base64 to display the initial and sign component to the user.
 * The base64 is stored in the `value` key of the pageComponent object.
 * We don't want to save the base64 in the backend.
 * @param pageComponent signature component
 * @returns For initials and sign components, value using 'name' key. For others, it will be 'value'.
 */

export const getContractInputValueUsingComponentType = (
  pageComponent: SignaturePageComponent,
) => {
  switch (pageComponent.componentType) {
    case SignatureComponentType.OWNER_INITIAL:
    case SignatureComponentType.OWNER_SIGN:
    case SignatureComponentType.REQUEST_INITIAL:
    case SignatureComponentType.REQUEST_SIGN:
      return pageComponent.name;
    case SignatureComponentType.OWNER_TEXT:
    case SignatureComponentType.OWNER_DATE:
    case SignatureComponentType.REQUEST_TEXT:
    case SignatureComponentType.REQUEST_DATE:
      return pageComponent.value;
    default:
      return ensureUnreachable(pageComponent.componentType);
  }
};
